// UserInfoUploader.js
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import {
    DialogContent, Button, DialogActions,
    Chip
} from '@mui/material';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import CloudUploadIcon from '@mui/icons-material/CloudUpload'; // upload file
import DoneIcon from '@mui/icons-material/Done'; // close the dialog.
import FindInPageIcon from '@mui/icons-material/FindInPage'; // search file
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant'; // error
import { useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { AppWord, AppStyle, TextToAuthClassId } from '../../value/AppConst';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import DraggableEditor from '../../common/DraggableEditor';
import { userInfoRepo, authTableTreeRepo } from '../../value/AppModel';
import { usePapaParse } from 'react-papaparse';
import { UserInfoDictionary } from './UserInfoDict';
import { UPLOAD_USER_INFO } from './UserInfoGql'; // 여기서 올리고, 여기서 반응을 보여줘야 함.
import IsValid from '../../value/IsValid';
import FlexyTable from '../../common/flexytable/FlexyTable';
import InputUtil from '../../value/InputUtil';
// import VzmCheckbox from '../../common/VzmCheckbox';
import ValueMisc from '../../value/ValueMisc';

const HeaderLine = styled(Box)({
    display: 'flex', alignItems: 'center', padding: 10, boxSizing: 'border-box',
});

const tableProps = UserInfoDictionary.uploadTableProps;

// extract for upload from InputMap
const { userId, authClassId, userName, password, zipCd, addr, telno, dcCd, storeId, comId } = UserInfoDictionary.inputMap;
const uploadMap = {
    userId, authClassId, userName, password, zipCd, addr, telno, dcCd, storeId, comId
};
const EmptyCsvData = {data:[], errors:[], meta:{}};

function checkItem(item) {
    const errors = [];
    for(const id of Object.keys(uploadMap)) {
        const label = uploadMap[id].label;
        if(IsValid.nullString(item[id])) {
            if(uploadMap[id].required) {
                errors.push(`${label} 없음.`);
            }
        }
        else {
            const check = uploadMap[id].check;
            if(check) {
                const good = check(item[id]);
                if(!good) {
                    errors.push(`${label} 오류.`);
                }
            }
        }
    }

    if(ValueMisc.isStoreClassId(item.authClassId)) {
        if(!item.storeId) errors.push(`점포코드 없음.`);
        else if(!/^\d+$/.test(item.storeId)) errors.push(`점포코드 형식 오류.`);
    }
    else if(ValueMisc.isTruckCompanyClassId(item.authClassId)) {
        if(!item.comId) errors.push(`운송사코드 없음.`);
        else if(IsValid.comId(item.comId) === false) errors.push(`운송사코드 형식 오류.`);
    }
    else {
        if(!item.dcCd) errors.push(`DC코드 없음.`);
        else if(!/^9\d\d$/.test(item.dcCd)) errors.push(`DC코드 형식 오류.`);
    }

    if(errors.length > 0) {
        item.ok = false;
        item.message = errors.join("\n");
    }
    else {
        item.ok = true;
        item.message = 'Ready';
    }
    return item;
}

// replace someKey with proper one
function checkAndMap(data) {
    let countError = 0;
    const records = data.map((array1) => {
        // Store	Login ID	Password	Zip.	Address	Phone	Level	DC	Store Code
        // "user_name", "user_id", "password","zip_cd","addr",
        // "full_tell_no","auth_name","dc_cd","store_id","com_id"
        const array = array1.map((cell) => cell ? cell.trim() : '');

        let item = {
            userName: array[0],
            userId: array[1],
            password: array[2],
            zipCd: array[3]==='-' ? '' : array[3],
            addr: array[4],
            telno: array[5]==='-' ? '' : array[5],
            authClassId: TextToAuthClassId[array[6] ? array[6].trim() : ''],
            dcCd: array[7]==='-' ? '' : array[7],
            storeId: array[8]==='-' ? '' : array[8],
            comId: array[9]==='-' ? '' : array[9],
        }
        if(!item.authClassId) {
            item.ok = false;
            item.message = '권한 미지정';
        }
        item = checkItem(item);
        if(!item.ok) countError++;
        return item;
    });
    return {records, countError};
}

/*
샘플 CSV:
사용자명,로그인ID,비밀번호,우편번호,주소,전화번호,권한,DC코드,점포코드,운송사코드
홍길동,hong,Abcdef1!,12345,서울시 강남구 역삼동 123-45,010-1234-5678,점포,,014,,
길동,honsg,Abcdef1!,12345,서울시 강남구 역삼동 123-45,010-1234-5678,운송사,,,,
김철수,kim,Abcdef1!,54321,서울시 강남구 역삼동 543-21,010-9876-5432,본사,982,,
김수,kism,Abcdef1!,54321,서울시 강남구 역삼동 543-21,010-9876-5432,본사,782,,

In pr_user_info_upload:
			IF p_auth_class_id='6' THEN
				IF p_store_id IS NOT NULL THEN
					SELECT EXISTS ( SELECT 1 FROM store_info WHERE store_id=p_store_id ) INTO v_store_exists;
					SET p_com_id=NULL;
					SET p_dc_cd=NULL;
					SET v_dsc_id=p_store_id;
				ELSE
					SET v_store_exists=0;
				END IF;
			ELSEIF p_auth_class_id='7' OR p_auth_class_id='8' THEN
				IF p_com_id IS NOT NULL THEN
					SELECT EXISTS ( SELECT 1 FROM com_info WHERE com_id=p_com_id ) INTO v_com_exists;
					SET p_store_id=NULL;
					SET p_dc_cd=NULL;
					SET v_dsc_id=p_com_id;
				ELSE
					SET v_com_exists=0;
				END IF;
			ELSE
				IF p_dc_cd IS NOT NULL THEN
					SELECT EXISTS ( SELECT 1 FROM dc_info WHERE dc_cd=p_dc_cd ) INTO v_dc_exists;
					SET p_com_id=NULL;
					SET p_store_id=NULL;
					SET v_dsc_id=p_dc_cd;
				ELSE
					SET v_dc_exists=0;
				END IF;
			END IF;

*/

export default function UserInfoUploader({
    open,
    csvText,
    isEucKr,
    onClose,
}) {
    const [text, setText] = useState('');
    const sessionInfo = useReactiveVar(userInfoRepo);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const [csvData, setCsvData] = useState(EmptyCsvData); // {data:[], errors:[], meta:{}}
    const [tableData, setTableData] = useState([]);
    const [errorCount, setErrorCount] = useState(0);
    const [changed, setChanged] = useState(false);
    const [msgDone, setMsgDone] = useState('');
    const addAlert = useAlertSetter();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const checkAuth = useAuthorizationCheck();
    const [useEucKr, setUseEucKr] = useState(false);
    const fileInputRef = useRef(null);
    const { readString } = usePapaParse();

    const [uploadUserInfo, responseUpload] = useMutation(UPLOAD_USER_INFO, {
        onCompleted: (data, option) => {onCompletedUpload(data, option);},
        onError: (error) => {
            addAlert({title: "사용자 정보 업로드 오류", message: [error.message]});
        }
    });

    useEffect(()=>{
        if(open) {
            setText(csvText);
            setUseEucKr(!!isEucKr);
        }
        else {
            setText('');
            setCsvData(EmptyCsvData);
        }
    }, [open, csvText, isEucKr]);

    // 내부에서 다시 파일 선택할 수 있으므로 csvText에서 직접 처리하지 않고 csvText => text => csvData로 처리.
    useEffect(()=>{
        if(!IsValid.nullString(text)) {
            readString(text, {
                worker: true,
                complete: (result) => {
                    const { data, errors, meta } = result;
                    data.shift(); // remove header
                    const records
                    = data //.filter((row) => /^\d{3}/.test(row[0]))
                    .map((row) => {
                        const newRow = row.slice(0, Object.keys(uploadMap).length);
                        // 추가작업 필요하면 여기에. (데이터 변경, 계산 등)

                        return newRow;
                    });
                    setCsvData({data: records, errors, meta});
                }
            })
        }
    }, [text, readString]);

    useEffect(()=>{
        if(open) {
            const {records, countError} = checkAndMap(csvData.data);
            setTableData(records);
            setMsgDone('');
            setErrorCount(countError);
            setChanged(true);
        }
    }, [csvData, sessionInfo, open]);

    const onCompletedUpload = (data, option) => {
        if(data.userInfoUpload.ok) {
            // reflect the result to the table.(ok, message, list {ok, message, arriveId})
            const newTableData = [];
            for(const prev of tableData) {
                const res = data.userInfoUpload.list.find((item)=>`${prev.userId}` === item.userId);
                if(res) {
                    prev.ok = res.ok;
                    prev.message = res.message;
                    newTableData.push(prev);
                }
                else {
                    prev.ok = false;
                    prev.message = '응답 없음';
                    newTableData.push(prev);
                }
            }
            setTableData(newTableData);
            setChanged(false); // 다시 올리지 않도록.
            enqueueSnackbar("사용자 정보 업로드 완료", {variant: 'success'});
            setMsgDone('[업로드 완료] ');
            // onClose();
        }
        else addAlert({title: "사용자 정보 업로드", message: [data.userInfoUpload.message]});
    };

    const onClickUpload = () => {
        // 아래 filter에서 "row &&" 빼면 오류 발생함. row가 널이거나 undefined인 경우가 없는데도 발생함. 헉!
        const toUpload = tableData.filter((row)=>{
            if(row && row.ok) return true;
            else return false;
        }).map((row)=>InputUtil.getDataToSubmit(row, uploadMap, false));
        const param = {variables: {userInfoList: toUpload}};

        uploadUserInfo(param);
    };

    const onClickFindFile = () => {
        if(fileInputRef.current) {
            fileInputRef.current.value='';
            fileInputRef.current.click();
        }
    };

    /* const onCsvFileRead = (event) => {
        const content = event.target.result;
        setText(content);
    }; */

    const onChooseFile = (event) => {
        if(event.target.files.length > 0) {
            const file = event.target.files[0];
                
            if (file) {
                InputUtil.readTextFileWithAutoEncoding(file, (content, isEucKr) => {
                    setUseEucKr(isEucKr);
                    setText(content);
                });
            }
        }
    };

    const alertErrorData = (row) => {
        const lines = row.message.split("\n").filter((line)=>line.length > 0);
        addAlert({title: `사용자 정보 오류(${row.dcCd}-${row.storeId})`, message: lines});
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case 'error': alertErrorData(row); break;
            default: break;
        }
    };

    const canUpload = checkAuth(authTableTree.tree.pr_user_info_upload);

    const tools = [
        {id:'error', icon: <NotificationImportantIcon/>, color: AppStyle.mui.error, where: (row) => !row.ok}
    ];

    return(
        <DraggableEditor open={open} maxWidth='md'
            onClose={onClose}
            startIcon={<CloudUploadIcon size={AppWord.LARGE} />}
            title="사용자 정보 업로드"
        >
            <DialogContent style={{position: 'relative', minHeight:'50vh', minWidth:600, width:'50vw'}}>
                <Box style={{position:'absolute', left:5, top:0, right:5, bottom:3, display:'flex', flexDirection: 'column'}}>
                    <HeaderLine>
                        <div style={{flexGrow:1, display:'flex', alignItems:'center'}}>
                        {msgDone && <ThumbUpAltIcon />}
                        {
                            tableData.length < 1 || !changed ?
                            <span>{msgDone}데이터 파일(CSV)을 선택해 주세요</span>
                            :
                            errorCount === 0 ?
                            <span>모든 데이터가 정상입니다. 업로드 버튼을 눌러주세요</span>
                            :
                            errorCount === tableData.length ?
                            <span>모든 데이터가 오류입니다. 수정해 주세요 (오류는 각 줄 끝 아이콘 눌러 확인)</span>
                            :
                            <span>오류가 없는 데이터만 올리려면 업로드 버튼을 눌러주세요 (오류는 각 줄 끝 아이콘 눌러 확인)</span>
                        }
                        </div>
                        {/* <div style={{fontSize:'0.8rem'}}>참조정보: {sessionInfo.user.dscIdAs}</div> */}
                    </HeaderLine>
                    <HeaderLine style={{borderTop:'1px solid #eee', color:'#f55'}}>
                        개인정보보호법 적용이 엄격해 짐에 따라 암호는 반드시 대문자, 소문자, 부호, 숫자 중 3개 이상을 섞어 8자 이상이어야 합니다.
                    </HeaderLine>
                    <FlexyTable
                        name={tableProps.tableId}
                        tableProps={tableProps}
                        records={tableData}
                        tools={tools}
                        onClickTool={onClickToolOnRow}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <input
                    type="file"
                    accept=".csv"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={onChooseFile}
                />
                {
                    /*
                    <VzmCheckbox
                        dict={{
                            id: 'useEucKr', label: '업로드 파일 완성형한글로 읽기',
                        }}
                        value={useEucKr}
                        onChange={(id,value,err) => setUseEucKr(value)}
                    />
                    */
                    fileInputRef.current && canUpload &&
                    <>
                    {
                        useEucKr ?
                        <Chip label="완성형한글로 읽음" color={AppStyle.mui.warning}
                            size={AppWord.SMALL}
                            style={{marginRight:10}}
                        />
                        :
                        tableData.length > 0 ?
                        <Chip label="UTF-8로 읽음" color={AppStyle.mui.success}
                            size={AppWord.SMALL}
                            style={{marginRight:10}}
                        /> : null
                    }
                    <Button
                        color={AppStyle.mui.secondary}
                        style={{marginRight:10}}
                        onClick={onClickFindFile}
                        startIcon={<FindInPageIcon />}
                        variant={AppStyle.mui.contained}
                        disabled={responseUpload.loading}
                    >
                        다른 파일 선택
                    </Button>
                    <span
                        style={{display:'inline-block', margin:'0px 20px 0px 10px', color:'#999'}}
                    >|</span>
                    </>
                }
                {
                    canUpload &&
                    <Button
                        color={AppStyle.mui.primary}
                        style={{marginRight:10}}
                        startIcon={<CloudUploadIcon />}
                        onClick={onClickUpload}
                        variant={AppStyle.mui.contained}
                        disabled={(errorCount > 0 && errorCount === tableData.length) || !changed || responseUpload.loading}
                    >
                        데이터 업로드
                    </Button>
                }
                <Button onClick={onClose} color={AppStyle.mui.primary}
                    variant={AppStyle.mui.contained}
                    startIcon={<DoneIcon />}
                    disabled={responseUpload.loading}
                >
                    닫기
                </Button>
            </DialogActions>
        </DraggableEditor>
    );
}